import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Mail, Phone, MapPin, ArrowRight } from 'lucide-react';
import { AbnyWebBadge } from '../common/AbnyWebBadge';

export const Footer: React.FC = () => {
  return (
    <footer className="bg-editorial-dark text-slate-300 pt-16 pb-12 border-t border-slate-800 font-sans">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Main 5-Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 pb-12 border-b border-slate-800/80">
          
          {/* Brand & Direct Contact Column */}
          <div className="lg:col-span-2 space-y-5">
            <div className="flex items-center gap-3.5">
              <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-brand-500/60 bg-white p-0.5 shadow-md flex items-center justify-center">
                <img
                  src="/ftt-logo.png"
                  alt="For The Truth Logo"
                  className="w-full h-full object-contain rounded-full"
                />
              </div>
              <div className="flex flex-col">
                <span className="font-extrabold text-base sm:text-lg tracking-tight text-white">
                  FOR THE TRUTH PRESS PRIVATE LIMITED
                </span>
                <span className="text-[10px] text-brand-400 font-mono font-bold tracking-widest uppercase">
                  Imprint: For The Truth Publishing
                </span>
              </div>
            </div>

            <p className="text-xs sm:text-sm text-slate-400 leading-relaxed max-w-sm">
              An independent theological publishing house and corporate entity committed to timeless biblical truth, theological fidelity, and rigorous editorial craft for India and the nations.
            </p>

            {/* Direct Contact Block */}
            <div className="p-4 rounded-xl bg-slate-900/90 border border-slate-800 space-y-2.5 text-xs">
              <span className="font-bold text-brand-300 uppercase tracking-wider text-[10px] block">
                Corporate &amp; Editorial Office
              </span>
              <div className="space-y-2">
                <a
                  href="tel:+918604685533"
                  className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
                >
                  <Phone className="w-3.5 h-3.5 text-brand-400 shrink-0" />
                  <span>Phone: <strong>+91 86046 85533</strong></span>
                </a>
                <a
                  href="mailto:contact@forthetruth.in"
                  className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
                >
                  <Mail className="w-3.5 h-3.5 text-brand-400 shrink-0" />
                  <span>Email: <strong>contact@forthetruth.in</strong></span>
                </a>
              </div>
            </div>

            {/* Ecosystem Architecture Note */}
            <div className="p-3.5 rounded-xl bg-slate-900/50 border border-slate-800/80 text-xs text-slate-400 space-y-1.5">
              <span className="font-semibold text-brand-400 uppercase tracking-wider text-[10px] block">
                Ecosystem Architecture
              </span>
              <p className="text-[11px] leading-relaxed">
                <strong>For The Truth Publishing</strong> is the author services, translation and editorial production arm of <strong>FOR THE TRUTH PRESS PRIVATE LIMITED</strong>. Retail book distribution is hosted through our parallel bookstore.
              </p>
              <a
                href="https://forthetruth.in/books"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-brand-400 hover:text-brand-300 font-semibold pt-1 text-xs"
              >
                <span>Visit For The Truth Bookstore</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>

          {/* Publishing Services */}
          <div>
            <h4 className="text-xs font-bold text-white tracking-wider uppercase mb-4 text-brand-200">
              Publishing Services
            </h4>
            <ul className="space-y-2.5 text-xs text-slate-400 font-medium">
              <li>
                <Link to="/services/manuscript-assessment" className="hover:text-white transition-colors">
                  Manuscript Assessment
                </Link>
              </li>
              <li>
                <Link to="/services/developmental-copy-editing" className="hover:text-white transition-colors">
                  Developmental &amp; Copy Editing
                </Link>
              </li>
              <li>
                <Link to="/services/cover-design-typesetting" className="hover:text-white transition-colors">
                  Cover Design &amp; Typesetting
                </Link>
              </li>
              <li>
                <Link to="/services/complete-publishing-management" className="hover:text-white transition-colors">
                  Complete Production Management
                </Link>
              </li>
              <li>
                <Link to="/our-process" className="hover:text-white transition-colors">
                  12-Stage Publishing Process
                </Link>
              </li>
              <li>
                <Link to="/submit-enquiry" className="text-brand-400 hover:text-brand-300 font-bold flex items-center gap-1 pt-1">
                  <span>Submit an RFQ / Enquiry</span>
                  <ArrowRight className="w-3 h-3" />
                </Link>
              </li>
            </ul>
          </div>

          {/* For Authors & Resources */}
          <div>
            <h4 className="text-xs font-bold text-white tracking-wider uppercase mb-4 text-brand-200">
              Authors &amp; Resources
            </h4>
            <ul className="space-y-2.5 text-xs text-slate-400 font-medium">
              <li>
                <Link to="/for-authors" className="hover:text-white transition-colors">
                  Author Submission Guidelines
                </Link>
              </li>
              <li>
                <Link to="/publications" className="hover:text-white transition-colors">
                  Published Works Catalog
                </Link>
              </li>
              <li>
                <Link to="/resources" className="hover:text-white transition-colors">
                  Manuscript Prep Guides
                </Link>
              </li>
              <li>
                <Link to="/news" className="hover:text-white transition-colors">
                  Publishing Insights &amp; News
                </Link>
              </li>
              <li>
                <Link to="/client/dashboard" className="hover:text-white transition-colors">
                  Author Client Portal
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-white transition-colors">
                  Editorial Committee Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Business & Legal Information */}
          <div>
            <h4 className="text-xs font-bold text-white tracking-wider uppercase mb-4 text-brand-200">
              Company &amp; Legal
            </h4>
            <ul className="space-y-2.5 text-xs text-slate-400 font-medium">
              <li>
                <Link to="/about" className="hover:text-white transition-colors">
                  About FOR THE TRUTH PRESS
                </Link>
              </li>
              <li>
                <Link to="/legal" className="hover:text-white transition-colors">
                  Publishing Terms &amp; Conditions
                </Link>
              </li>
              <li>
                <Link to="/legal" className="hover:text-white transition-colors">
                  Author Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/legal" className="hover:text-white transition-colors">
                  Copyright &amp; Permissions
                </Link>
              </li>
              <li>
                <Link to="/login" className="hover:text-white transition-colors">
                  Staff &amp; Client Login
                </Link>
              </li>
            </ul>
          </div>

        </div>

        {/* Bottom Bar: Copyright & Exact Styled ABNY Web Pill Badge */}
        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-400 font-medium">
          <div className="flex flex-col sm:flex-row items-center gap-2 text-center sm:text-left">
            <p>
              &copy; {new Date().getFullYear()} <strong className="text-white">FOR THE TRUTH PRESS PRIVATE LIMITED</strong>. All rights reserved.
            </p>
          </div>

          {/* Pixel-Perfect ABNY Web Button matching user screenshot */}
          <AbnyWebBadge />

          <div className="flex items-center gap-6">
            <Link to="/about" className="hover:text-slate-200 transition-colors">About</Link>
            <Link to="/services" className="hover:text-slate-200 transition-colors">Services</Link>
            <Link to="/legal" className="hover:text-slate-200 transition-colors">Legal</Link>
            <Link to="/contact" className="hover:text-slate-200 transition-colors">Contact</Link>
          </div>
        </div>

      </div>
    </footer>
  );
};
